import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  validatePassword,
  createSession,
  getSession,
  isSessionValid,
  clearSession,
  refreshSession
} from '../services/auth';

// Create the Auth Context
const AuthContext = createContext(null);

/**
 * AuthProvider component - Wraps the app to provide authentication state
 */
export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = () => {
      if (isSessionValid()) {
        const session = getSession();
        setIsAuthenticated(true);
        setUser(session.username);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
      setIsLoading(false);
    };

    checkExistingSession();
  }, []);

  // Refresh session periodically to keep it alive while user is active
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      if (!refreshSession()) {
        // Session expired, log out
        setIsAuthenticated(false);
        setUser(null);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  /**
   * Login function - validates credentials and creates session
   * @param {string} username - Username to log in with
   * @param {string} password - Password to validate
   * @returns {object} Result object with success boolean and optional error message
   */
  const login = useCallback((username, password) => {
    // Validate inputs
    if (!username || !username.trim()) {
      return { success: false, error: 'Username is required' };
    }

    if (!password) {
      return { success: false, error: 'Password is required' };
    }

    // Validate password against env variable
    if (!validatePassword(password)) {
      return { success: false, error: 'Invalid password' };
    }

    // Create session and update state
    createSession(username.trim());
    setIsAuthenticated(true);
    setUser(username.trim());

    return { success: true };
  }, []);

  /**
   * Logout function - clears session and resets state
   */
  const logout = useCallback(() => {
    clearSession();
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  // Context value
  const value = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to use the Auth context
 * @returns {object} Auth context value
 */
export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export default AuthContext;
